import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getAuth } from 'firebase/auth';
import { UserSettings, SystemPromptData, ChatPayload, ChatResponse, Patient, ReportDataPayload, TherapistApplication, UserDoc, TimelineEntry, SavedMemory } from '../../shared/types/src';


// ======================================================================================
// ||| AXIOS CLIENT CONFIGURATION (INTERNAL)                                          |||
// ======================================================================================



const apiClient = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
    headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const user = getAuth().currentUser;
    if (user) {
        const token = await user.getIdToken();
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Read the active role from sessionStorage.
    const activeUserRole = sessionStorage.getItem('activeUserRole');
    if (activeUserRole) {
        config.headers['X-Active-Role'] = activeUserRole;
    } else {
        console.warn(`X-Active-Role header not set for request to ${config.url}`);
    }

    // Check if a child account is active and add the X-Child-Id header
    const activeChildId = sessionStorage.getItem('activeChildId');
    if (activeChildId) {
        config.headers['X-Child-Id'] = activeChildId;
    }

    return config;
});

apiClient.interceptors.response.use((response: AxiosResponse) => {
    // Note: Backend now sends camelCase, so no transformation needed
    return response;
});


// ======================================================================================
// ||| API SERVICE (PUBLIC)                                                           |||
// ======================================================================================
// ||| This service acts as an adapter, providing named methods that use the        |||
// ||| configured apiClient internally. It maintains compatibility with legacy code.    |||
// ======================================================================================

class ApiService {
    private readonly endpoints = {
        CHAT: '/api/chat',
        CHAT_HISTORY: '/api/chat-history',
        MEMORY: '/api/memory',
        SYSTEM_PROMPT: '/api/system-prompt',
        TOKENS: '/api/tokens',
        TOKEN_PACKAGES: '/api/token-packages',
        USER: '/api/users',
        SAVED_MEMORY: '/api/memory/users',
        TIMELINE: '/api/memory/users',
        GET_USER: '/api/users',
        SETTINGS: '/api/users',
        LINK_PATIENT: '/api/linkPatientToTherapist',
        LINK_PATIENT_WITH_CODE: '/api/link-patient-with-code',
        SEND_REFLECTION: '/api/sendReflectionToTherapist',
        GENERATE_INVITE_CODE: '/api/generate-invite-code',
        JOURNAL: '/api/journal',
    };

    private getCurrentUserId(): string {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
            throw new Error('No authenticated user');
        }
        return user.uid;
    }

    // Generic request method for flexibility
    async makeRequest<T>(endpoint: string, options?: AxiosRequestConfig): Promise<T> {
        try {
            const response = await apiClient.request<T>({ url: endpoint, ...options });
            return response.data;
        } catch (error: any) {
            // If backend is not available, return empty/mock data instead of failing
            if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.response?.status === 503) {
                console.warn(`Backend unavailable for ${endpoint}, returning empty response`);
                return {} as T;
            }
            
            // For other errors, still throw to maintain existing behavior
            throw error;
        }
    }

    async callFunction<T>(endpointKey: keyof typeof this.endpoints, data: any): Promise<T> {
        const endpointPath = this.endpoints[endpointKey];
        if (!endpointPath) {
            throw new Error(`Invalid endpoint key provided: ${endpointKey}`);
        }

        const response = await apiClient.post(endpointPath, { data });
        return response.data.data as T;
    }

    // --- CHAT ---
    async sendChatMessage(payload: ChatPayload): Promise<ChatResponse> {
        try {
            const user = this.getCurrentUserId();
            if (!user) throw new Error('User not authenticated');
            
            const { data } = await apiClient.post<ChatResponse>(this.endpoints.CHAT, {
                message: payload.message,
                model: payload.model,
                content_mode: payload.contentMode,
                mindstream_mode: payload.mindstreamMode,
                local_history: payload.localHistory,
                user_id: payload.userId,
                therapist_instructions: null
            });
            return data;
        } catch (error: any) {
            // If backend is not available, return a mock response
            if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.response?.status === 503) {
                console.warn('Backend unavailable for chat, returning mock response');
                            return {
                response: "I'm currently in offline mode. Your message has been saved locally and will be processed when the backend is available.",
                modelUsed: payload.model,
                tokensUsed: 0,
                meta: {
                    tone: 'neutral',
                    reason: 'offline_mode',
                    ethics: [],
                    historical_symbolic_tags: []
                }
            };
            }
            throw error;
        }
    }

    async getChatHistory(userId: string): Promise<{ history: any[] }> {
        const { data } = await apiClient.get(`${this.endpoints.CHAT_HISTORY}/${userId}`);
        return data;
    }

    // --- MEMORY & TIMELINE ---
    async saveMemory(userId: string, content: string, tags: string[]) {
        const { data } = await apiClient.post(this.endpoints.MEMORY, {
            user_id: userId,
            content,
            tags,
        });
        return data;
    }

    async getMemories(userId: string) {
        const { data } = await apiClient.get(`${this.endpoints.MEMORY}/${userId}`);
        return data;
    }

    async getPinnedTimeline(userId: string): Promise<TimelineEntry[]> {
        const { data } = await apiClient.get<TimelineEntry[]>(`${this.endpoints.TIMELINE}/${userId}/timeline`);
        return data;
    }

    async getSavedMemories(userId: string): Promise<SavedMemory[]> {
        const { data } = await apiClient.get<SavedMemory[]>(`${this.endpoints.SAVED_MEMORY}/${userId}/saved-memories`);
        return data;
    }

    // --- USER & SETTINGS ---
    async getUser(userId: string): Promise<UserDoc> {
        const { data } = await apiClient.get<UserDoc>(`${this.endpoints.GET_USER}/${userId}`);
        return data;
    }

    async getUserSettings(userId: string): Promise<UserSettings> {
        const { data } = await apiClient.get<UserSettings>(`${this.endpoints.USER}/${userId}/settings`);
        return data;
    }

    async updateUserSettings(userId: string, settings: Partial<UserSettings>): Promise<UserSettings> {
        console.log('apiService.updateUserSettings called with:', { userId, settings });
        console.log('apiService.updateUserSettings - settings type:', typeof settings);
        console.log('apiService.updateUserSettings - settings keys:', Object.keys(settings));
        
        const { data } = await apiClient.patch<UserSettings>(`${this.endpoints.USER}/${userId}/settings`, settings);
        console.log('apiService.updateUserSettings - response data:', data);
        return data;
    }

    async updateUserRole(userId: string, newRole: string): Promise<{ message: string }> {
        const { data } = await apiClient.patch(`${this.endpoints.USER}/${userId}`, { role: newRole });
        return data;
    }

    async getUserData(userId: string): Promise<any> {
        const { data } = await apiClient.get(`${this.endpoints.USER}/${userId}`);
        return data;
    }

    async getSystemStatus(): Promise<any> {
        const { data } = await apiClient.get('/system/status');
        return data;
    }

    // --- TOKENS ---
    async getTokenBalance(childAccountId?: string) {
        const headers: any = {};
        if (childAccountId) {
            headers['X-Child-Account-Id'] = childAccountId;
        }
        const { data } = await apiClient.get('/api/tokens/balance', { headers });
        return data;
    }

    async getTokenSummary(userId: string) {
        const { data } = await apiClient.get(`/api/users/${userId}/token-summary`);
        return data;
    }

    async getTokenPackages() {
        const { data } = await apiClient.get(this.endpoints.TOKEN_PACKAGES);
        return data;
    }

    async createCheckoutSession(packageId: string) {
        const { data } = await apiClient.post(`${this.endpoints.TOKEN_PACKAGES}/checkout`, {
            package_id: packageId,
        });
        return data;
    }

    // --- SYSTEM PROMPT ---
    async getSystemPrompt(model: string): Promise<SystemPromptData> {
        const contentMode = localStorage.getItem('contentMode');
        const params = contentMode ? `?content_mode=${contentMode}` : '';
        const { data } = await apiClient.get<SystemPromptData>(`${this.endpoints.SYSTEM_PROMPT}/${model}${params}`);
        return data;
    }

    // --- SYMBOLIC TAGS ---
    async getSymbolicTags(userId: string) {
        try {
            const { data } = await apiClient.get<{ tags: string[] }>(`/api/symbolic-tags/${userId}`);
            return data.tags;
        } catch (error: any) {
            // If backend is not available, return empty array
            if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.response?.status === 503) {
                console.warn('Backend unavailable for symbolic tags, returning empty array');
                return [];
            }
            throw error;
        }
    }

    // --- PREDICTIVE INSIGHTS ---
    async getPredictiveInsights(days: number = 7) {
        const { data } = await apiClient.get(`/api/metrics/predictive-insights?days=${days}`);
        return data;
    }

    // --- REPORTS ---
    async generateWeeklyReport() {
        const { data } = await apiClient.post('/api/reports/generate-weekly');
        return data;
    }

    async listUserReports(limit: number = 10) {
        const { data } = await apiClient.get(`/api/reports/list?limit=${limit}`);
        return data;
    }

    async downloadReport(reportId: string) {
        const { data } = await apiClient.get(`/api/reports/${reportId}/download`);
        return data;
    }

    async scheduleWeeklyReports(scheduleData: {
        enabled: boolean;
        frequency: 'weekly' | 'monthly';
        dayOfWeek?: number;
        emailDelivery?: boolean;
        emailAddress?: string;
    }) {
        const { data } = await apiClient.post('/api/reports/schedule', scheduleData);
        return data;
    }

    // --- THERAPIST & ADMIN ---
    async generateInviteCode(): Promise<{ code: string }> {
        const { data } = await apiClient.post<{ code: string }>(this.endpoints.GENERATE_INVITE_CODE);
        return data;
    }

    async linkPatientWithCode(code: string): Promise<{ message: string }> {
        const { data } = await apiClient.post<{ message: string }>(this.endpoints.LINK_PATIENT_WITH_CODE, { code });
        return data;
    }

    async getTherapistPatients(therapistId: string): Promise<{ patients: Patient[] }> {
        const { data } = await apiClient.get(`/api/therapist/${therapistId}/patients`);
        return data;
    }

    async getPendingApplications(): Promise<TherapistApplication[]> {
        const { data } = await apiClient.get<TherapistApplication[]>('/admin/pending-applications');
        return data;
    }

    async approveTherapist(userId: string): Promise<{ message: string }> {
        const { data } = await apiClient.post<{ message: string }>(`/admin/applications/${userId}/approve`);
        return data;
    }

    async submitTherapistApplication(applicationData: any): Promise<{ message: string }> {
        const { data } = await apiClient.post<{ message: string }>('/api/therapist-application', applicationData);
        return data;
    }

    async generatePatientSummary(patientId: string): Promise<{ summary: string; createdAt: string; modelUsed: string; }> {
        const { data } = await apiClient.post(`/api/therapist/patients/${patientId}/generate-summary`);
        return data;
    }

    async getSymbolicTrends(patientId: string): Promise<any> {
        const { data } = await apiClient.get(`/api/therapist/patients/${patientId}/symbolic-trends`);
        return data;
    }

    async getMySymbolicTrends(): Promise<any> {
        try {
            const { data } = await apiClient.get('/api/metrics/symbolic-trends');
            return data;
        } catch (error: any) {
            // If backend is not available, return empty trends
            if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.response?.status === 503) {
                console.warn('Backend unavailable for symbolic trends, returning empty trends');
                return { trends: [] };
            }
            throw error;
        }
    }

    async getPatientAlerts(therapistId: string, patientId: string): Promise<{ alerts: any[] }> {
        const { data } = await apiClient.get(`/api/therapist/${therapistId}/patients/${patientId}/alerts`);
        return data;
    }

    async markAlertAsRead(therapistId: string, patientId: string, alertId: string): Promise<{ message: string }> {
        const { data } = await apiClient.post(`/api/therapist/${therapistId}/patients/${patientId}/alerts/${alertId}/read`);
        return data;
    }

    async getReportData(therapistId: string, patientId: string, startDate: string, endDate: string): Promise<ReportDataPayload> {
        const { data } = await apiClient.post<ReportDataPayload>(`/api/therapist/${therapistId}/patients/${patientId}/report-data`, {
            start_date: startDate,
            end_date: endDate
        });
        return data;
    }

    async injectPrompt(patientId: string, prompt: string): Promise<{ message: string }> {
        const { data } = await apiClient.post(`/api/therapist/patients/${patientId}/inject-prompt`, { prompt });
        return data;
    }

    async post<T>(endpoint: string, data: any): Promise<T> {
        const response = await apiClient.post<T>(endpoint, data);
        return response.data;
    }

    async getAlertsSummary(therapistId: string): Promise<{ totalAlerts: number; unreadAlerts: number }> {
        const { data } = await apiClient.get(`/api/therapist/${therapistId}/alerts/summary`);
        return data;
    }

    async getAllPatientAlerts(therapistId: string): Promise<any[]> {
        const { data } = await apiClient.get(`/api/therapist/${therapistId}/alerts/all`);
        return data;
    }

    async getClinicalSettings(therapistId: string): Promise<any> {
        const { data } = await apiClient.get(`/api/therapist/${therapistId}/clinical-settings`);
        return data;
    }

    async updateClinicalSettings(therapistId: string, settings: any): Promise<any> {
        const { data } = await apiClient.post(`/api/therapist/${therapistId}/clinical-settings`, settings);
        return data;
    }

    async saveReportSchedule(therapistId: string, schedule: {
        isEnabled: boolean;
        frequency: 'weekly' | 'monthly';
        patientIds: string[];
        sendTime: string;
    }): Promise<{ status: string; message: string; }> {
        const { data } = await apiClient.post(`/api/therapist/${therapistId}/scheduled-reports`, schedule);
        return data;
    }

    async dismissPatientAlert(patientId: string) {
        const { data } = await apiClient.post(`/patients/${patientId}/dismiss-alert`);
        return data;
    }

    async markTherapistInjectionAsUsed(userId: string) {
        const { data } = await apiClient.post(`${this.endpoints.USER}/${userId}/mark-injection-used`);
        return data;
    }

    // --- SYMBOLIC OVERRIDE ---
    async submitSymbolicOverride(overrideData: {
        userId: string;
        messageId: string;
        originalTags: string[];
        correctedTags: string[];
        timestamp: string;
    }): Promise<{ message: string }> {
        const { data } = await apiClient.post('/api/symbolic-override', overrideData);
        return data;
    }

    // --- RADAR METRICS ---
    async getRadarMetrics(): Promise<{
        axes: Record<string, number>;
        source_tags: string[];
        is_synthetic: boolean;
    }> {
        try {
            const { data } = await apiClient.get('/api/metrics/radar-chart');
            return data;
        } catch (error: any) {
            // If backend is not available, return neutral scores
            if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.response?.status === 503) {
                console.warn('Backend unavailable for radar metrics, returning neutral scores');
                return {
                    axes: {
                        ethical_balance: 50.0,
                        personalization: 50.0,
                        wisdom: 50.0,
                        activity: 50.0,
                        sleep: 50.0,
                        concentration: 50.0,
                        energy: 50.0,
                        self_esteem: 50.0,
                        emotional_clarity: 50.0
                    },
                    source_tags: [],
                    is_synthetic: true
                };
            }
            throw error;
        }
    }

    // --- UNIFIED SYMBOLIC SCORING ---
    async getUnifiedSymbolicScoring(params: {
        userId?: string;
        testMode?: boolean;
        days?: number;
    } = {}): Promise<{
        userId: string;
        radarScores: Record<string, number>;
        sourceTags: string[];
        tagFrequency: Record<string, number>;
        patternInsights: any[];
        isSynthetic: boolean;
        timestamp: string;
        analysisPeriod: {
            startDate: string;
            endDate: string;
            days: number;
        };
        debug?: any;
    }> {
        try {
            const { data } = await apiClient.post('/api/metrics/symbolic-scoring', params);
            return data;
        } catch (error: any) {
            // If backend is not available, return synthetic data
            if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.response?.status === 503) {
                console.warn('Backend unavailable for unified symbolic scoring, returning synthetic data');
                return {
                userId: params.userId || 'unknown',
                    radarScores: {
                        ethical_balance: 50.0,
                        personalization: 50.0,
                        wisdom: 50.0,
                        activity: 50.0,
                        sleep: 50.0,
                        concentration: 50.0,
                        energy: 50.0,
                        self_esteem: 50.0,
                        emotional_clarity: 50.0
                    },
                    sourceTags: [],
                    tagFrequency: {},
                    patternInsights: [],
                    isSynthetic: true,
                    timestamp: new Date().toISOString(),
                    analysisPeriod: {
                        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
                        endDate: new Date().toISOString(),
                        days: params.days || 7
                    }
                };
            }
            throw error;
        }
    }

    // --- INSIGHT FEEDBACK ---
    async submitInsightFeedback(feedbackData: {
        userId: string;
        insightId: string;
        feedback: 'accurate' | 'inaccurate';
        timestamp: string;
    }): Promise<{ message: string }> {
        const { data } = await apiClient.post('/api/insight-feedback', feedbackData);
        return data;
    }

    // --- WELLNESS GIFT SYSTEM ---
    async submitGiftRequest(requestData: any): Promise<any> {
        const { data } = await apiClient.post('/api/wellness-gift/request', requestData);
        return data;
    }

    async getGiftHistory(userId: string): Promise<any[]> {
        const { data } = await apiClient.get(`/api/wellness-gift/history/${userId}`);
        return data;
    }

    async submitAppeal(giftId: string, appealData: any): Promise<any> {
        const { data } = await apiClient.post(`/api/wellness-gift/appeal/${giftId}`, appealData);
        return data;
    }

    async getPendingGiftRequests(): Promise<any> {
        const { data } = await apiClient.get('/api/wellness-gift/pending');
        return data;
    }

    async batchReviewRequests(reviewData: any): Promise<any> {
        const { data } = await apiClient.post('/api/wellness-gift/batch-review', reviewData);
        return data;
    }

    async getGiftAnalytics(): Promise<any> {
        const { data } = await apiClient.get('/api/wellness-gift/analytics');
        return data;
    }

    async reviewGiftRequest(giftId: string, reviewData: any): Promise<any> {
        const { data } = await apiClient.post(`/api/wellness-gift/review/${giftId}`, reviewData);
        return data;
    }

    // --- SPRINT 10: SYMBOLIC GOALS ---
    async createSymbolicGoal(goalData: {
        userId: string;
        theme: string;
        description?: string;
    }): Promise<any> {
        const { data } = await apiClient.post('/api/goals', goalData);
        return data;
    }

    async getUserGoals(userId: string): Promise<any[]> {
        const { data } = await apiClient.get(`/api/goals/${userId}`);
        return data;
    }

    async updateSymbolicGoal(goalId: string, updateData: {
        theme?: string;
        description?: string;
        isActive?: boolean;
    }): Promise<any> {
        const { data } = await apiClient.patch(`/api/goals/${goalId}`, updateData);
        return data;
    }

    async deleteSymbolicGoal(goalId: string): Promise<{ message: string }> {
        const { data } = await apiClient.delete(`/api/goals/${goalId}`);
        return data;
    }

    async getGoalAlignment(userId: string, weekStart?: string): Promise<any> {
        const params = weekStart ? { week_start: weekStart } : {};
        const { data } = await apiClient.get(`/api/goals/${userId}/alignment`, { params });
        return data;
    }

    // --- SPRINT 10: PATIENT SUMMARIES ---
    async generateWeeklyPatientSummary(patientId: string, weekStart: string): Promise<any> {
        const { data } = await apiClient.post(`/api/patient-summary/generate/${patientId}`, {
            week_start: weekStart
        });
        return data;
    }

    async getPatientSummaries(patientId: string): Promise<any[]> {
        const { data } = await apiClient.get(`/api/patient-summary/${patientId}/summaries`);
        return data;
    }

    async exportSummaryToPdf(summaryId: string): Promise<Blob> {
        const { data } = await apiClient.post(`/api/patient-summary/export/${summaryId}`, {}, {
            responseType: 'blob'
        });
        return data;
    }

    // --- SPRINT 10: THERAPIST SUGGESTIONS ---
    async scheduleSuggestion(suggestionData: {
        therapistId: string;
        patientId: string;
        promptType: 'reflective' | 'insight' | 'encouragement';
        content: string;
        scheduledFor: string;
    }): Promise<any> {
        const { data } = await apiClient.post('/api/therapist/suggestions', suggestionData);
        return data;
    }

    async getPatientSuggestions(patientId: string): Promise<any[]> {
        const { data } = await apiClient.get(`/api/therapist/suggestions/${patientId}`);
        return data;
    }

    async updateSuggestion(suggestionId: string, updateData: {
        content?: string;
        scheduledFor?: string;
        promptType?: 'reflective' | 'insight' | 'encouragement';
    }): Promise<any> {
        const { data } = await apiClient.patch(`/api/therapist/suggestions/${suggestionId}`, updateData);
        return data;
    }

    async cancelSuggestion(suggestionId: string): Promise<{ message: string }> {
        const { data } = await apiClient.delete(`/api/therapist/suggestions/${suggestionId}`);
        return data;
    }

    async getSuggestionTemplates(): Promise<any[]> {
        const { data } = await apiClient.get('/api/therapist/suggestions/templates');
        return data;
    }

    async deliverPendingSuggestions(): Promise<{ message: string }> {
        const { data } = await apiClient.post('/api/therapist-suggestions/deliver-pending');
        return data;
    }

    // --- SPRINT 11: GUIDED INTERVENTIONS ---
    async getAvailableInterventions(): Promise<any[]> {
        const { data } = await apiClient.get('/api/interventions');
        return data;
    }

    async getInterventionDetails(interventionId: string): Promise<any> {
        const { data } = await apiClient.get(`/api/interventions/${interventionId}`);
        return data;
    }

    async startIntervention(interventionId: string): Promise<any> {
        const { data } = await apiClient.post(`/api/interventions/${interventionId}/start`);
        return data;
    }

    async completeIntervention(interventionId: string, responses: any[]): Promise<any> {
        const { data } = await apiClient.post(`/api/interventions/${interventionId}/complete`, { responses });
        return data;
    }

    async getUserInterventions(): Promise<any[]> {
        const { data } = await apiClient.get('/api/interventions/user/progress');
        return data;
    }

    async checkInterventionTriggers(): Promise<any[]> {
        const { data } = await apiClient.get('/api/interventions/triggers/check');
        return data;
    }

    async assignInterventionToPatient(patientId: string, interventionId: string): Promise<any> {
        const { data } = await apiClient.post(`/api/interventions/therapist/patients/${patientId}/interventions/${interventionId}/assign`);
        return data;
    }

    async getPatientInterventions(patientId: string): Promise<any[]> {
        const { data } = await apiClient.get(`/api/interventions/therapist/patients/${patientId}/interventions`);
        return data;
    }

    // --- SPRINT 11: MOOD REPLAY ---
    async generateMoodReplay(startDate: string, endDate: string): Promise<any> {
        const { data } = await apiClient.post('/api/mood-replay/generate', { start_date: startDate, end_date: endDate });
        return data;
    }

    async getMoodReplay(replayId: string): Promise<any> {
        const { data } = await apiClient.get(`/api/mood-replay/${replayId}`);
        return data;
    }

    async getReplayPlaybackData(replayId: string): Promise<any> {
        const { data } = await apiClient.get(`/api/mood-replay/${replayId}/playback`);
        return data;
    }

    async getMoodReplayHistory(limit: number = 10): Promise<any[]> {
        const { data } = await apiClient.get(`/api/mood-replay/history?limit=${limit}`);
        return data;
    }

    async deleteMoodReplay(replayId: string): Promise<{ message: string }> {
        const { data } = await apiClient.delete(`/api/mood-replay/${replayId}`);
        return data;
    }

    async getWeeklyReplay(): Promise<any> {
        const { data } = await apiClient.get('/api/mood-replay/quick/weekly');
        return data;
    }

    async getMonthlyReplay(): Promise<any> {
        const { data } = await apiClient.get('/api/mood-replay/quick/monthly');
        return data;
    }

    // --- SPRINT 11: MEMORY ANCHORS ---
    async createMemoryAnchor(anchorData: {
        title: string;
        date: string;
        tagsPresent: string[];
        reflectionNote?: string;
        sourceType: 'chat_message' | 'insight' | 'tag_cluster';
        sourceId?: string;
        radarScores: Record<string, number>;
    }): Promise<any> {
        const { data } = await apiClient.post('/api/anchors', anchorData);
        return data;
    }

    async getUserAnchors(categoryId?: string, limit: number = 50): Promise<any[]> {
        const params = new URLSearchParams();
        if (categoryId) params.append('category_id', categoryId);
        params.append('limit', limit.toString());
        const { data } = await apiClient.get(`/api/anchors?${params.toString()}`);
        return data;
    }

    async getAnchorDetails(anchorId: string): Promise<any> {
        const { data } = await apiClient.get(`/api/anchors/${anchorId}`);
        return data;
    }

    async updateAnchor(anchorId: string, updates: any): Promise<any> {
        const { data } = await apiClient.patch(`/api/anchors/${anchorId}`, updates);
        return data;
    }

    async deleteAnchor(anchorId: string): Promise<{ message: string }> {
        const { data } = await apiClient.delete(`/api/anchors/${anchorId}`);
        return data;
    }

    async getAnchorCategories(): Promise<any[]> {
        const { data } = await apiClient.get('/api/anchors/categories');
        return data;
    }

    async createAnchorCategory(categoryData: {
        name: string;
        color: string;
    }): Promise<any> {
        const { data } = await apiClient.post('/api/anchors/categories', categoryData);
        return data;
    }

    async updateAnchorCategory(categoryId: string, updates: any): Promise<any> {
        const { data } = await apiClient.patch(`/api/anchors/categories/${categoryId}`, updates);
        return data;
    }

    async deleteAnchorCategory(categoryId: string): Promise<{ message: string }> {
        const { data } = await apiClient.delete(`/api/anchors/categories/${categoryId}`);
        return data;
    }

    async searchAnchors(query: string, limit: number = 20): Promise<any[]> {
        const { data } = await apiClient.get(`/api/anchors/search?query=${encodeURIComponent(query)}&limit=${limit}`);
        return data;
    }

    async getAnchorsByTag(tag: string, limit: number = 20): Promise<any[]> {
        const { data } = await apiClient.get(`/api/anchors/tags/${encodeURIComponent(tag)}?limit=${limit}`);
        return data;
    }

    // --- JOURNAL ---
    async createJournalEntry(entryData: {
        userId: string;
        text: string;
        contentMode?: string;
    }): Promise<any> {
        const { data } = await apiClient.post(`${this.endpoints.JOURNAL}/entries`, {
            user_id: entryData.userId,
            text: entryData.text,
            content_mode: entryData.contentMode || 'FULL'
        });
        return data;
    }

    async getJournalEntries(userId: string, limit: number = 20): Promise<any[]> {
        const { data } = await apiClient.get(`${this.endpoints.JOURNAL}/entries/${userId}?limit=${limit}`);
        return data;
    }

    async getJournalSummaries(userId: string, limit: number = 10): Promise<any[]> {
        const { data } = await apiClient.get(`${this.endpoints.JOURNAL}/summaries/${userId}?limit=${limit}`);
        return data;
    }

    async getJournalPrivacySettings(userId: string): Promise<any> {
        const { data } = await apiClient.get(`${this.endpoints.JOURNAL}/privacy-settings/${userId}`);
        return data;
    }

    async updateJournalPrivacySettings(settings: {
        userId: string;
        parentId: string;
        accessLevel: 'none' | 'summary' | 'full';
        allowAiReflection: boolean;
        allowSymbolicTags: boolean;
    }): Promise<any> {
        const { data } = await apiClient.post(`${this.endpoints.JOURNAL}/privacy-settings`, {
            user_id: settings.userId,
            parent_id: settings.parentId,
            access_level: settings.accessLevel,
            allow_ai_reflection: settings.allowAiReflection,
            allow_symbolic_tags: settings.allowSymbolicTags
        });
        return data;
    }

    // --- ANCHOR CATEGORIES ---
}

// Export a single instance for the entire application
const apiService = new ApiService();
export default apiService;

// Also export apiClient for direct use with react-query
export { apiClient };